import React, { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Popover from '@material-ui/core/Popover';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ThemeSwitcher from '../ThemeSwitcher';

// Only the tools that actually exist in this app, styled like the Coolors
// "Tools" mega-menu.
const FEATURES = [
  {
    to: '/generate',
    title: 'Palette Generator',
    desc: 'Create unique color palettes quickly and effortlessly.',
    color: '#3b82f6',
    exact: false,
  },
  {
    to: '/explore',
    title: 'Explore Palettes',
    desc: 'Browse hundreds of curated palettes by category.',
    color: '#14b8a6',
    exact: false,
  },
  {
    to: '/contrast-checker',
    title: 'Contrast Checker',
    desc: 'Ensure your designs meet WCAG accessibility standards.',
    color: '#ec4899',
    exact: false,
  },
  {
    to: '/palette/new',
    title: 'Create Palette',
    desc: 'Start a fresh palette in the generator.',
    color: '#f59e0b',
    exact: false,
  },
];

const useStyles = makeStyles(theme => ({
  nav: {
    display: 'flex',
    alignItems: 'center',
    padding: '0.5rem 1.5rem',
    borderBottom: `1px solid ${(theme.palette.divider) || 'rgba(0,0,0,0.08)'}`,
    backgroundColor: theme.palette.background ? theme.palette.background.paper : '#fff',
    position: 'sticky',
    top: 0,
    zIndex: 1200,
    minHeight: '56px',
    boxSizing: 'border-box',
  },
  logo: {
    fontWeight: 800,
    fontSize: '1.3rem',
    letterSpacing: '-0.03em',
    textDecoration: 'none',
    color: theme.palette.text ? theme.palette.text.primary : '#111',
  },
  spacer: { flexGrow: 1 },
  toolsButton: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.95rem',
    color: theme.palette.text ? theme.palette.text.primary : '#111',
  },
  popoverPaper: {
    padding: '1.5rem',
    maxWidth: '640px',
    borderRadius: '12px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1.25rem 2rem',
    '@media (max-width: 600px)': {
      gridTemplateColumns: '1fr',
    },
  },
  card: {
    textDecoration: 'none',
    display: 'block',
  },
  cardTitle: {
    fontWeight: 800,
    fontSize: '1.05rem',
    marginBottom: '0.25rem',
  },
  cardDesc: {
    fontSize: '0.8rem',
    color: theme.palette.text ? theme.palette.text.secondary : '#666',
    lineHeight: 1.35,
  },
}));

/**
 * Slim sticky top bar with a Coolors-style "Tools" mega-dropdown that links
 * to every tool the app actually provides.
 */
function GlobalNav() {
  const classes = useStyles();
  const [anchor, setAnchor] = useState(null);

  const open = Boolean(anchor);

  return (
    <header className={classes.nav}>
      <Link to="/" className={classes.logo}>
        customcolor
      </Link>

      <div className={classes.spacer} />

      <Button
        className={classes.toolsButton}
        endIcon={<ExpandMoreIcon />}
        onClick={e => setAnchor(e.currentTarget)}
        aria-label="Tools"
      >
        Tools
      </Button>
      <ThemeSwitcher />

      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        classes={{ paper: classes.popoverPaper }}
      >
        <div className={classes.grid}>
          {FEATURES.map(f => (
            <NavLink
              key={f.to}
              to={f.to}
              exact={f.exact}
              className={classes.card}
              onClick={() => setAnchor(null)}
            >
              <div className={classes.cardTitle} style={{ color: f.color }}>
                {f.title}
              </div>
              <div className={classes.cardDesc}>{f.desc}</div>
            </NavLink>
          ))}
        </div>
      </Popover>
    </header>
  );
}

export default GlobalNav;
